import React, { useState, useEffect } from 'react';
import api from './axiosConfig';
import './ForecastModals.css';

const SundayPlanningInterface = ({ onClose, onSave }) => {
  const [staff, setStaff] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [staffLoading, setStaffLoading] = useState(true);

  const days = [
    { id: 1, name: 'Monday', short: 'Mon' },
    { id: 2, name: 'Tuesday', short: 'Tue' },
    { id: 3, name: 'Wednesday', short: 'Wed' },
    { id: 4, name: 'Thursday', short: 'Thu' },
    { id: 5, name: 'Friday', short: 'Fri' },
    { id: 6, name: 'Saturday', short: 'Sat' },
    { id: 7, name: 'Sunday', short: 'Sun' }
  ];

  useEffect(() => {
    fetchStaff();
    setDefaultWeek();
  }, []);

  const setDefaultWeek = () => {
    const today = new Date();
    const nextMonday = new Date();
    nextMonday.setDate(today.getDate() + (1 + 7 - today.getDay()) % 7);
    setSelectedWeek(nextMonday.toISOString().split('T')[0]);
  };

  const fetchStaff = async () => {
    try {
      setStaffLoading(true);
      setError(null);
      
      const response = await api.get('/employees/active');
      
      const employeeData = response.data.data || response.data;
      const revenueStaff = Array.isArray(employeeData) 
        ? employeeData.filter(member => member.generates_revenue !== false)
        : [];
      
      if (revenueStaff.length === 0) {
        setError('No active revenue-generating staff found. Please add staff members first.');
        setStaff([]);
        return;
      }
      
      setStaff(revenueStaff);
      initializeForecasts(revenueStaff);
      
    } catch (error) {
      console.error('Error fetching staff:', error);
      
      if (error.response?.status === 404) {
        setError('Staff endpoint not found. Please check your API configuration.');
      } else if (error.response?.status === 401) {
        setError('Not authorized to access staff data.');
      } else if (error.response?.status === 500) {
        setError('Server error loading staff. Please try again later.');
      } else {
        setError('Failed to load staff list. Please check your connection and try again.');
      }
      
      setStaff([]);
    } finally {
      setStaffLoading(false);
    }
  };

  const initializeForecasts = (staffList) => {
    const initialForecasts = [];
    staffList.forEach(staffMember => {
      days.forEach(day => {
        initialForecasts.push({
          staff_member_id: staffMember.id,
          staff_name: staffMember.name,
          day_of_week: day.id,
          day_name: day.name,
          forecast_revenue: 0,
          forecast_appointments: 0,
          notes: ''
        });
      });
    });
    setForecasts(initialForecasts);
  };

  const updateForecast = (staffId, dayId, field, value) => {
    setForecasts(prev => prev.map(forecast => 
      forecast.staff_member_id === staffId && forecast.day_of_week === dayId
        ? { ...forecast, [field]: value }
        : forecast
    ));
  };

  const copyPreviousWeek = async () => {
    try {
      setLoading(true);
      
      const currentWeekStart = new Date(selectedWeek);
      const previousWeekStart = new Date(currentWeekStart);
      previousWeekStart.setDate(currentWeekStart.getDate() - 7);
      
      const response = await api.get(`/forecast/week/${previousWeekStart.toISOString().split('T')[0]}`);
      
      if (response.data && response.data.length > 0) {
        const updatedForecasts = [...forecasts];
        
        response.data.forEach(previousForecast => {
          const forecastIndex = updatedForecasts.findIndex(f => 
            f.staff_member_id === previousForecast.staff_member_id && 
            f.day_of_week === previousForecast.day_of_week
          );
          
          if (forecastIndex !== -1) {
            updatedForecasts[forecastIndex] = {
              ...updatedForecasts[forecastIndex],
              forecast_revenue: previousForecast.forecast_revenue || 0,
              forecast_appointments: previousForecast.forecast_appointments || 0
            };
          }
        });
        
        setForecasts(updatedForecasts);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError('No previous week data found to copy.');
        setTimeout(() => setError(null), 3000);
      }
      
    } catch (error) {
      console.error('Error copying previous week:', error);
      setError('Failed to load previous week data.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const saveForecast = async () => {
      console.log("🟢 NEW CODE: saveForecast called - forecasts count:", forecasts.length);
    try {
      setLoading(true);
      setError(null);

      const validForecasts = forecasts.filter(f => 
        (parseFloat(f.forecast_revenue) || 0) > 0 || 
        (parseInt(f.forecast_appointments) || 0) > 0
      );

      if (validForecasts.length === 0) {
        setError('Please enter at least one forecast value before saving.');
        setLoading(false);
        return;
      }

      const forecastData = {
        week_start_date: selectedWeek,
        forecasts: validForecasts.map(f => ({
          staff_member_id: f.staff_member_id,
          day_of_week: f.day_of_week,
          forecast_revenue: parseFloat(f.forecast_revenue) || 0,
          forecast_appointments: parseInt(f.forecast_appointments) || 0,
          notes: f.notes || ''
        }))
      };

      await api.post('/forecast/weekly', forecastData);

      setSuccess(true);
      setTimeout(() => {
        onSave?.();
        onClose?.();
      }, 1500);

    } catch (error) {
      console.error('Error saving forecast:', error);
      
      if (error.response?.status === 400) {
        setError('Invalid forecast data. Please check your entries.');
      } else if (error.response?.status === 409) {
        setError('Forecast already exists for this week. Please edit the existing forecast.');
      } else {
        setError('Failed to save forecast data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTotalForDay = (dayId) => {
    return forecasts
      .filter(f => f.day_of_week === dayId)
      .reduce((sum, f) => sum + (parseFloat(f.forecast_revenue) || 0), 0);
  };

  const getTotalForStaff = (staffId) => {
    return forecasts
      .filter(f => f.staff_member_id === staffId)
      .reduce((sum, f) => sum + (parseFloat(f.forecast_revenue) || 0), 0);
  };

  const getWeekTotal = () => {
    return forecasts.reduce((sum, f) => sum + (parseFloat(f.forecast_revenue) || 0), 0);
  };

  if (staffLoading) {
    return (
      <div className="forecast-modal-overlay">
        <div className="forecast-modal-container">
          <div className="forecast-modal-header">
            <div className="forecast-modal-title">
              <div className="forecast-modal-icon purple">
                <svg width="24" height="24" fill="none" stroke="#8b5cf6" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="forecast-modal-h2">Sunday Planning</h2>
                <p className="forecast-modal-subtitle">Loading your team data...</p>
              </div>
            </div>
            <button onClick={onClose} className="forecast-modal-close">
              <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="forecast-modal-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <div className="forecast-spinner"></div>
            <span style={{ marginLeft: '12px' }}>Loading staff data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forecast-modal-overlay">
      <div className="forecast-modal-container">
        <div className="forecast-modal-header">
          <div className="forecast-modal-title">
            <div className="forecast-modal-icon purple">
              <svg width="24" height="24" fill="none" stroke="#8b5cf6" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="forecast-modal-h2">Sunday Planning</h2>
              <p className="forecast-modal-subtitle">Set weekly forecasts for your team</p>
            </div>
          </div>
          <button onClick={onClose} className="forecast-modal-close">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="forecast-modal-content">
          {error && (
            <div className="forecast-alert forecast-alert-error">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="forecast-alert forecast-alert-success">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Forecast data saved successfully!</span>
            </div>
          )}

          {staff.length === 0 && !error && (
            <div className="forecast-alert" style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>No active staff members found. Please add staff members to your team first.</span>
            </div>
          )}

          {staff.length > 0 && (
            <>
              <div className="forecast-grid forecast-grid-4" style={{ marginBottom: '24px' }}>
                <div className="forecast-form-group">
                  <label className="forecast-form-label">Week Starting (Monday)</label>
                  <input
                    type="date"
                    value={selectedWeek}
                    onChange={(e) => setSelectedWeek(e.target.value)}
                    className="forecast-form-input"
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'end' }}>
                  <button
                    onClick={copyPreviousWeek}
                    disabled={loading}
                    className="forecast-btn forecast-btn-secondary forecast-btn-full"
                  >
                    Copy Previous Week
                  </button>
                </div>
                <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'end' }}>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>Week Total</p>
                  <p className="forecast-summary-value purple">�{getWeekTotal().toFixed(2)}</p>
                </div>
              </div>

              <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '24px' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table className="forecast-planning-table">
                    <thead className="forecast-table-header">
                      <tr>
                        <th>Staff Member</th>
                        {days.map(day => (
                          <th key={day.id}>
                            <div>{day.short}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>�{getTotalForDay(day.id).toFixed(0)}</div>
                          </th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staff.map(staffMember => (
                        <tr key={staffMember.id} className="forecast-table-row">
                          <td className="forecast-table-cell">
                            <div className="forecast-staff-info">
                              <div className="forecast-staff-avatar">
                                <svg width="16" height="16" fill="#8b5cf6" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <div className="forecast-staff-details">
                                <h4>{staffMember.name}</h4>
                                <p>�{getTotalForStaff(staffMember.id).toFixed(2)}</p>
                              </div>
                            </div>
                          </td>
                          {days.map(day => {
                            const forecast = forecasts.find(f => 
                              f.staff_member_id === staffMember.id && f.day_of_week === day.id
                            );
                            return (
                              <td key={day.id} className="forecast-table-cell">
                                <div className="forecast-input-group">
                                  <input
                                    type="number"
                                    placeholder="�"
                                    value={forecast?.forecast_revenue || ''}
                                    onChange={(e) => updateForecast(
                                      staffMember.id, 
                                      day.id, 
                                      'forecast_revenue', 
                                      e.target.value
                                    )}
                                    className="forecast-input-small"
                                    min="0"
                                    step="0.01"
                                  />
                                  <input
                                    type="number"
                                    placeholder="Appts"
                                    value={forecast?.forecast_appointments || ''}
                                    onChange={(e) => updateForecast(
                                      staffMember.id, 
                                      day.id, 
                                      'forecast_appointments', 
                                      e.target.value
                                    )}
                                    className="forecast-input-small"
                                    min="0"
                                  />
                                </div>
                              </td>
                            );
                          })}
                          <td className="forecast-table-cell">
                            <span style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              padding: '2px 10px', 
                              borderRadius: '9999px', 
                              fontSize: '12px', 
                              fontWeight: '500', 
                              backgroundColor: '#f3e8ff', 
                              color: '#8b5cf6' 
                            }}>
                              �{getTotalForStaff(staffMember.id).toFixed(0)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="forecast-grid forecast-grid-3" style={{ marginBottom: '24px' }}>
                <div className="forecast-summary-card purple">
                  <div className="forecast-summary-value purple">�{getWeekTotal().toFixed(2)}</div>
                  <div className="forecast-summary-label">Total Week Forecast</div>
                </div>
                <div className="forecast-summary-card green">
                  <div className="forecast-summary-value green">
                    {forecasts.filter(f => (parseFloat(f.forecast_revenue) || 0) > 0).length}
                  </div>
                  <div className="forecast-summary-label">Scheduled Sessions</div>
                </div>
                <div className="forecast-summary-card blue">
                  <div className="forecast-summary-value blue">�{(getWeekTotal() / 7).toFixed(0)}</div>
                  <div className="forecast-summary-label">Daily Average</div>
                </div>
              </div>

              <div className="forecast-tips">
                <h3>Planning Tips</h3>
                <ul>
                  <li>Consider historical data and booking patterns</li>
                  <li>Account for holidays, events, and seasonal variations</li>
                  <li>Set realistic but motivating targets for each team member</li>
                  <li>Review and adjust based on actual performance</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="forecast-modal-footer">
          <button
            onClick={onClose}
            disabled={loading}
            className="forecast-btn forecast-btn-secondary"
          >
            Cancel
          </button>
          {staff.length > 0 && (
            <button
              onClick={saveForecast}
              disabled={loading || getWeekTotal() === 0}
              className="forecast-btn forecast-btn-primary"
            >
              {loading ? (
                <>
                  <div className="forecast-spinner"></div>
                  Saving...
                </>
              ) : (
                <>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Save Weekly Forecast
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SundayPlanningInterface;